import { ChangeDetectionStrategy, Component, computed, effect, input, signal, untracked } from '@angular/core';
import { TimeService } from '@bayclubs/utils';
import * as moment from 'moment';

import { DatePickerModel } from '../date-picker.component';

@Component({
    selector: 'app-year-picker',
    templateUrl: './year-picker.component.html',
    styleUrls: ['./year-picker.component.scss', '../picker-form.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class YearPickerComponent {
    readonly isOpen = input(false);
    readonly min = input<moment.Moment | undefined>(undefined);
    readonly max = input<moment.Moment | undefined>(undefined);
    readonly model = input.required<DatePickerModel>();

    private readonly yearsPerPage = 16;
    private readonly lastYearInMatrix = signal<moment.Moment | null>(null);

    readonly years = computed<moment.Moment[]>(() => {
        const end = this.lastYearInMatrix();
        if (!end) return [];
        const result: moment.Moment[] = [];
        let cur = this.timeService.addYears(end, -this.yearsPerPage + 1);
        while (cur <= end) {
            if (this.is29February() && cur.year() % 4 === 0) {
                cur = this.timeService.addDays(cur, -1);
            }
            result.push(cur);
            if (this.is29February() && cur.year() % 4 === 0) {
                cur = this.timeService.addDays(cur, 1);
            }
            cur = this.timeService.addYears(cur, 1);
        }
        return result;
    });

    readonly isNextPage = computed(() => {
        const ys = this.years();
        const max = this.max();
        if (ys.length === 0 || !max) return false;
        return Math.max(...ys.map(x => x.year())) < max.year();
    });

    readonly isPreviousPage = computed(() => {
        const ys = this.years();
        const min = this.min();
        if (ys.length === 0 || !min) return false;
        return min.year() < Math.min(...ys.map(x => x.year()));
    });

    constructor(private timeService: TimeService) {
        // Re-center matrix on isOpen change (matches old ngOnChanges behavior).
        effect(() => {
            const open = this.isOpen();
            const max = this.max();
            untracked(() => {
                if (this.lastYearInMatrix() === null && max) {
                    this.lastYearInMatrix.set(max);
                }
                if (open) {
                    this.lastYearInMatrix.set(this.computeLastYearMatrix());
                }
            });
        });
    }

    private computeLastYearMatrix(): moment.Moment {
        const m = this.model();
        const modelYear = m.year();
        const month = m.month();
        const day = m.day();
        let last: moment.Moment;
        const cur = this.lastYearInMatrix();
        if (cur) {
            last = cur.clone();
        } else {
            const max = this.max();
            last = max ? this.timeService.startOfYear(max) : this.timeService.toMoment(new Date());
        }
        if (month != null) {
            last = this.timeService.startOfYear(last);
            last = this.timeService.addMonths(last, month);
        }
        if (day != null) {
            last = this.timeService.startOfMonth(last);
            last = this.timeService.addDays(last, day - 1);
        }
        if (modelYear != null) {
            while (modelYear > last.year()) {
                last = this.timeService.addYears(last, this.yearsPerPage);
            }
            while (last.year() - this.yearsPerPage >= modelYear) {
                last = this.timeService.addYears(last, -this.yearsPerPage);
            }
        }
        return last;
    }

    public selectYear(newDate: moment.Moment): void {
        if (this.isYearDisabled(newDate)) return;
        const m = this.model();
        const cloned = newDate.clone();
        m.date.set(cloned);
        m.year.set(cloned.year());
        this.clearCells(newDate);
    }

    clearCells(newDate: moment.Moment): void {
        const m = this.model();
        const month = m.month();
        const date = m.date();

        if (month != null && date && (this.isSelectedMinorMonthThanMinMarginal() || this.isSelectedGreaterMonthThanMaxMarginal())) {
            m.month.set(null);
            let newD = this.timeService.startOfYear(date);
            const currentDay = m.day();
            if (currentDay) {
                newD = this.timeService.addDays(newD, currentDay - 1);
            }
            m.date.set(newD);
        }
        const date2 = m.date();
        const day2 = m.day();
        if (day2 != null && date2 && (date2.daysInMonth() < day2 || this.isDayDisabled(date2))) {
            m.day.set(null);
            m.date.set(this.timeService.startOfMonth(date2));
        } else if (this.is29February() && newDate.year() % 4 !== 0 && date2) {
            m.day.set(null);
            const newD = this.timeService.startOfMonth(date2);
            newD.subtract(1, 'month');
            m.date.set(newD);
        }
    }

    is29February(): boolean {
        const m = this.model();
        return m.day() === 29 && m.month() === 1;
    }

    isDayDisabled(date: moment.Moment): boolean {
        return this.isSelectedMinorDayThanMinMarginal(date) || this.isSelectedGreaterDayThanMaxMarginal(date);
    }

    isSelectedGreaterDayThanMaxMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const max = this.max();
        return y != null && max != null && y === max.year() && date.month() === max.month() && date.date() > max.date();
    }

    isSelectedMinorDayThanMinMarginal(date: moment.Moment): boolean {
        const y = this.model().year();
        const min = this.min();
        return y != null && min != null && y === min.year() && min.month() === date.month() && min.date() > date.date();
    }

    isSelectedGreaterMonthThanMaxMarginal(): boolean {
        const m = this.model();
        const max = this.max();
        const month = m.month();
        const year = m.year();
        return max != null && year != null && month != null && max.year() === year && month > max.month();
    }

    isSelectedMinorMonthThanMinMarginal(): boolean {
        const m = this.model();
        const min = this.min();
        const month = m.month();
        const year = m.year();
        return min != null && year != null && month != null && min.year() === year && min.month() > month;
    }

    undoPage(): void {
        if (this.isPreviousPage()) {
            const cur = this.lastYearInMatrix();
            if (cur) {
                this.lastYearInMatrix.set(this.timeService.addYears(cur, -this.yearsPerPage));
            }
        }
    }

    addPage(): void {
        if (this.isNextPage()) {
            const cur = this.lastYearInMatrix();
            if (cur) {
                this.lastYearInMatrix.set(this.timeService.addYears(cur, this.yearsPerPage));
            }
        }
    }

    public isSelected(date: moment.Moment): boolean {
        return this.model().year() === date.year();
    }

    public isYearDisabled(date: moment.Moment): boolean {
        const min = this.min();
        const max = this.max();
        return (min != null && min.year() > date.year()) || (max != null && date.year() > max.year());
    }

    trackByYear = (_: number, y: moment.Moment) => y.year();
}
